import { useState, useEffect } from "react"
import CROController from "@controllers/cro.controller"
import { Link, useNavigate } from "react-router-dom"
import AuthLayout from "../../../components/layout/AuthLayout"
import Button from "../../../components/ui/Button"
import FormInput from "../../../components/layout/FormInput"
import State, { STATE, CurrentEntityState } from "@state"
import NavigationService from "@services/navigation.service.js"
import { useSession } from "@hooks/useSession";

export default function CROCreateCompanySignUp() {
  const navigate = useNavigate();
  const [isSubmitted, setIsSubmitted] = useState(false)
 
  const [isLoading, setIsLoading] = useState(false)
  const [formData, setFormData] = useState({
    companyName: "",
    street: "",
    number: "",
    country: "",
    city: "",
    postalCode: "",
    regNumber: "",
    contactNumber: "",
  })
  const [error, setError] = useState(null)
  const [countdown, setCountdown] = useState(2)
  const { session, user, clearSession } = useSession();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isLoading) return;
    setError(null);

    // Simple validation: ensure required fields not empty
    const missing = Object.entries(formData).filter(([k, v]) => !v.trim());
    if (missing.length) {
      setError(`Please fill ${missing[0][0]} field`);
      return;
    }

    setIsLoading(true);
    const result = await CROController.createCompany(formData);
    setIsLoading(false);
    if (result?.success) {
      setIsSubmitted(true);
      // Could redirect after countdown
      setTimeout(() => NavigationService.navigateTo("/dashboard"), 3000);
    } else {
      setError(result?.message || "Unable to submit");
    }
  };


  useEffect(() => {
    // Listen for entity list changes to refresh link visibility
    const onEntitiesChange = (newVal) => {
      setEntitiesCount(Array.isArray(newVal) ? newVal.length : 0);
    };
    State.registerListener(CurrentEntityState.ENTITIES_LIST, onEntitiesChange);

    return () => State.unregisterListener(CurrentEntityState.ENTITIES_LIST, onEntitiesChange);
  }, []);

  useEffect(() => {
    if(!session){
      NavigationService.navigateTo("/");
    }
    
  }, [user, session]);


  return (
    <AuthLayout
      title=""
      subtitle=""
      logoSrc="/img/logo.png"
      imageSrc="/img/Oshimed sign in image - earth.png"
      formWidth="half"
      contentPadding="small"
      maxWidth="full"
    >
      {isSubmitted ? (
        <div className="text-center py-8">
          <div className="w-16 h-16 bg-green-100 rounded-full mx-auto flex items-center justify-center mb-4">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-8 w-8 text-green-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Company information submitted!</h3>
          <p className="text-sm text-gray-600 mb-4">Thank you for providing your company details.</p>
          <p className="text-sm text-gray-600">
            You will be redirected to the email verification page in <span className="font-bold">{countdown}</span>{" "}
            seconds...
          </p>
        </div>
      ) : (
        <>
          <form className="space-y-6 w-full" onSubmit={handleSubmit}>
            {/* Company name - full width */}
             <FormInput
               label="Company name"
               id="companyName"
               name="companyName"
               type="text"
               placeholder="Oshimed SA"
               required
               value={formData.companyName}
               onChange={handleChange}
             />

            {/* Two column layout */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Company street - 2/3 width */}
              <div className="md:col-span-2">
                 <FormInput
                   label="Company street"
                   id="street"
                   name="street"
                   type="text"
                   placeholder="Avenue of modern clinical studies"
                   required
                   value={formData.street}
                   onChange={handleChange}
                 />
              </div>

              {/* Number - 1/3 width */}
               <FormInput label="Number" id="number" name="number" type="text" placeholder="123" required value={formData.number} onChange={handleChange} />
            </div>

            {/* Three column layout */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Country */}
              <FormInput label="Country" id="country" name="country" type="text" placeholder="France" required value={formData.country} onChange={handleChange} />

              {/* City/Town */}
              <FormInput label="City / Town" id="city" name="city" type="text" placeholder="Paris" required value={formData.city} onChange={handleChange} />

              {/* Postal code */}
              <FormInput
                 label="Postal code"
                 id="postalCode"
                 name="postalCode"
                 type="text"
                 placeholder="75000"
                 required
                 value={formData.postalCode}
                 onChange={handleChange}
               />
            </div>

            {/* Two column layout */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Company registration number */}
               <FormInput
                 label="Company registration number"
                 id="regNumber"
                 name="regNumber"
                 type="text"
                 placeholder="0706 696 076"
                 required
                 value={formData.regNumber}
                 onChange={handleChange}
               />

              {/* Contact number */}
               <FormInput
                 label="Contact number"
                 id="contactNumber"
                 name="contactNumber"
                 type="tel"
                 placeholder="+1 24 61 23 76 54"
                 required
                 value={formData.contactNumber}
                 onChange={handleChange}
               />
            </div>

            {/* Continue button */}
            <div className="mt-6 flex justify-center">
              <Button
                 type="submit"
                 variant="login"
                 disabled={isLoading}
                 className={`bg-button-login text-white font-medium py-3 flex justify-center ${isLoading ? 'opacity-60 cursor-not-allowed' : ''}`}
               >
                 {isLoading ? (
                   <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                     <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                     <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z" />
                   </svg>
                 ) : (
                   'Continue'
                 )}
               </Button>
            </div>
           {error && <p className="text-red-500 text-sm text-center">{error}</p>}
           </form>
          <div className="h-4"></div>

          {/* Terms and conditions */}
          <div className="mt-6 text-center">
            <p className="text-gray-600">
              By creating an account, you agree to Oshimed's{" "}
              <Link to="/conditions-of-use" className="text-linkColor font-semibold hover:underline">
                Conditions of use
              </Link>
            </p>
          </div>
            
              <div className="mt-4 text-center">
                <Link to="/dashboard" className="text-linkColor font-semibold hover:underline">
                  Skip and go to dashboard
                </Link>
              </div>
          
        </>
      )}
    </AuthLayout>
  )
}
